<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import {
  getStageColorClasses,
  stageColorStyle,
} from 'dashboard/routes/dashboard/pipeline/components/pipelineTemplates';

const props = defineProps({
  stages: {
    type: Array,
    required: true,
  },
  currentStageId: {
    type: String,
    required: true,
  },
  isWon: {
    type: Boolean,
    default: false,
  },
  isLost: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['moveToStage', 'requestTerminalMove']);

const { t } = useI18n();

// ── Derived ───────────────────────────────────────────────────────────────────

const currentIndex = computed(() =>
  props.stages.findIndex(stage => stage.id === props.currentStageId)
);

const totalStages = computed(() => props.stages.length);

const funnelPercent = computed(() => {
  if (totalStages.value === 0) return 0;
  if (currentIndex.value < 0) return 0;
  return Math.round(((currentIndex.value + 1) / totalStages.value) * 100);
});

const funnelProgressColor = computed(() => {
  if (props.isWon) return 'teal';
  if (props.isLost) return 'ruby';
  return 'brand';
});

// ── Stage state helpers ───────────────────────────────────────────────────────

const stageIndex = stage =>
  props.stages.findIndex(item => item.id === stage.id);

const isCurrent = stage => stage.id === props.currentStageId;

// Só etapas NÃO terminais anteriores à atual foram de fato percorridas. Um
// terminal irmão não alcançado (ex.: "Perdido" quando o card está em "Ganho")
// nunca deve herdar a cor do desfecho — bug real: `isWon`/`isLost` coloriam
// TODAS as etapas (inclusive a terminal que não foi escolhida) porque a
// checagem antiga não olhava se a própria etapa era terminal.
const isNonTerminalPast = stage =>
  !stage.terminal && stageIndex(stage) < currentIndex.value;

// ── Node visual classes ───────────────────────────────────────────────────────

// Cor fora das 5 conhecidas não tem classe — a etapa ATUAL do stepper ficava
// sem a cor escolhida. Só vale quando a etapa é a atual; nos demais estados o
// stepper usa cores semânticas próprias (concluído, perdido, pendente).
const nodeStyle = stage =>
  isCurrent(stage) && stage?.color
    ? stageColorStyle(stage.color, 'badge')
    : undefined;

const nodeClass = stage => {
  if (isCurrent(stage) && props.isWon) {
    return 'bg-n-teal-9 text-white border-n-teal-9';
  }
  if (isCurrent(stage) && props.isLost) {
    return 'bg-n-ruby-9 text-white border-n-ruby-9';
  }
  if (isCurrent(stage)) {
    return stage.color
      ? getStageColorClasses(stage.color).badge
      : 'bg-n-brand text-white border-n-brand';
  }
  if (isNonTerminalPast(stage) && props.isLost) {
    return 'bg-n-ruby-9 text-white border-n-ruby-9';
  }
  if (isNonTerminalPast(stage)) {
    // Em progresso (sem desfecho ainda) ou já Ganho — nos dois casos as
    // etapas reais já percorridas ficam em teal.
    return 'bg-n-teal-9 text-white border-n-teal-9';
  }
  return 'bg-n-alpha-2 text-n-slate-9 border-n-weak';
};

// Checkmark: etapas reais já percorridas, OU a própria etapa atual quando o
// card terminou em Ganho/Perdido.
const showCheck = stage =>
  isNonTerminalPast(stage) ||
  (isCurrent(stage) && (props.isWon || props.isLost));

// ── Connector line classes ────────────────────────────────────────────────────

// Um conector só representa um caminho real quando a etapa à esquerda foi
// percorrida (não-terminal, já ultrapassada) E a etapa à direita continua o
// caminho: outra etapa não-terminal, ou o terminal efetivamente alcançado.
// Isso evita colorir o trecho Ganho↔Perdido (nunca é uma transição real) ou
// o trecho que levaria ao terminal NÃO escolhido.
const connectorClass = stage => {
  if (!isNonTerminalPast(stage)) return 'border-n-weak';

  const nextStage = props.stages[stageIndex(stage) + 1];
  const continuesPath =
    nextStage &&
    (!nextStage.terminal ||
      (isCurrent(nextStage) && (props.isWon || props.isLost)));
  if (!continuesPath) return 'border-n-weak';

  if (props.isLost) return 'border-n-ruby-9';
  return props.isWon ? 'border-n-teal-9' : 'border-n-brand';
};

// ── Stage label ───────────────────────────────────────────────────────────────

const stageLabel = stage => {
  if (isCurrent(stage)) {
    if (props.isWon)
      return `${stage.name} (${t('PIPELINE.STEPPER.WON_BADGE')})`;
    if (props.isLost)
      return `${stage.name} (${t('PIPELINE.STEPPER.LOST_BADGE')})`;
  }
  return stage.name;
};

// ── Click handler ─────────────────────────────────────────────────────────────

const handleStageClick = stage => {
  if (props.disabled) return;
  if (isCurrent(stage)) return;
  if (stage.terminal) {
    emit('requestTerminalMove', stage.id);
  } else {
    emit('moveToStage', stage.id, props.currentStageId);
  }
};

// ── Aria label ────────────────────────────────────────────────────────────────

const ariaLabel = stage => t('PIPELINE.STEPPER.MOVE_TO', { stage: stage.name });
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <!-- ── Stepper track ── -->
    <div class="overflow-x-auto">
      <div class="flex items-start min-w-max px-1 py-2">
        <template v-for="(stage, index) in stages" :key="stage.id">
          <!-- ── Stage node + label ── -->
          <div class="flex flex-col items-center gap-1.5 relative">
            <!-- Node button -->
            <button
              type="button"
              :data-stage-id="stage.id"
              class="size-11 rounded-full flex items-center justify-center border-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1"
              :class="[
                nodeClass(stage),
                {
                  'cursor-default': isCurrent(stage) || disabled,
                  'cursor-pointer hover:opacity-80':
                    !isCurrent(stage) && !disabled,
                  'ring-4 ring-n-brand/20 motion-safe:animate-pulse':
                    isCurrent(stage) && !isWon && !isLost,
                },
              ]"
              :style="nodeStyle(stage)"
              :aria-label="ariaLabel(stage)"
              :aria-current="isCurrent(stage) ? 'step' : undefined"
              :disabled="disabled || isCurrent(stage)"
              @click="handleStageClick(stage)"
            >
              <!-- Checkmark for completed / won / lost-past -->
              <svg
                v-if="showCheck(stage)"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>

              <!-- Current stage dot -->
              <span
                v-else-if="isCurrent(stage)"
                class="w-3 h-3 rounded-full bg-current"
                aria-hidden="true"
              />

              <!-- Future stage number -->
              <span
                v-else
                class="text-xs font-medium leading-none"
                aria-hidden="true"
              >
                {{ index + 1 }}
              </span>
            </button>

            <!-- Stage label — largura maior + quebra de linha em vez de
                 truncar: nomes como "Contato Realizado" viravam "Contato
                 Re..." e o usuário só via o nome completo passando o mouse. -->
            <span
              class="text-xs text-center leading-tight max-w-[104px]"
              :class="[
                isCurrent(stage)
                  ? 'font-medium text-n-slate-12'
                  : 'text-n-slate-10',
                isWon && isCurrent(stage) ? 'text-n-teal-11' : '',
                isLost && isCurrent(stage) ? 'text-n-ruby-11' : '',
              ]"
              :title="stageLabel(stage)"
            >
              {{ stageLabel(stage) }}
            </span>

            <!-- Current indicator -->
            <div
              v-if="isCurrent(stage)"
              class="flex flex-col items-center gap-0.5 -mt-0.5"
            >
              <!-- Upward triangle -->
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="currentColor"
                class="text-n-brand"
                aria-hidden="true"
              >
                <path d="M5 0L10 6H0L5 0Z" />
              </svg>
              <span class="text-[10px] font-medium text-n-brand leading-none">
                {{ t('PIPELINE.STEPPER.CURRENT') }}
              </span>
            </div>
          </div>

          <!-- ── Connector line (not after last node) ── -->
          <div
            v-if="index < stages.length - 1"
            class="flex-1 h-0 mt-5 border-t-2 min-w-[24px]"
            :class="connectorClass(stage)"
            aria-hidden="true"
          />
        </template>
      </div>
    </div>

    <!-- ── Funnel progress ── -->
    <div v-if="totalStages > 0" class="flex flex-col gap-1 px-1">
      <ProgressBar
        :value="funnelPercent"
        :max="100"
        size="sm"
        :color="funnelProgressColor"
        :label="
          t('PIPELINE.STEPPER.FUNNEL_PROGRESS', { percent: funnelPercent })
        "
      />
      <span class="text-xs text-n-slate-10 self-end" aria-hidden="true">
        {{ t('PIPELINE.STEPPER.FUNNEL_PROGRESS', { percent: funnelPercent }) }}
      </span>
    </div>
  </div>
</template>
